/**
 * Посылка на экране (ISSUE-118): кнопка над свободной частью поля, картинка и анимация — в CSS, бой её не видит.
 * Точка задана долями зоны `.parcel-zone`, а зона обходит HUD, поэтому посылка не падает под кнопки на любом экране.
 */
import { balanceV1 } from "@newfungame/engine";
import { useEffect, useState } from "preact/hooks";
import { analytics } from "../../../analytics";
import { useStore } from "../../../app/hooks";
import { appStore, battleSpeed, battleStore } from "../../../app/store";
import { hintsStore, HintMark } from "../../hints";
import { formatCompact, Icon } from "../../kit";
import type { HudKey, HudT } from "../messages";
import { createParcelWatch, type ParcelToast, type ParcelView } from "./watch";
import "./parcel.css";

const LIFE_TICKS = balanceV1.parcels?.lifeTicks ?? 0;
const IMAGES = ["/assets/parcel/parcel-chute.webp", "/assets/parcel/parcel-box.webp"] as const;

function Toast({ toast, t }: { toast: ParcelToast; t: HudT }) {
  return (
    <span
      key={toast.seq}
      class="parcel-toast"
      data-testid="parcel-toast"
      data-reward={toast.reward}
      style={{ left: `${toast.left}%`, top: `${toast.top}%` }}
    >
      {toast.reward === "coins" ? (
        <>
          <Icon name="coin" size={22} />
          {t("hud.parcelCoins", { n: formatCompact(toast.coins) })}
        </>
      ) : (
        t(`hud.parcelBoost.${toast.boostId}` as HudKey)
      )}
    </span>
  );
}

export function ParcelLayer({ onTap, t }: { onTap(id: number): void; t: HudT }) {
  const [view, setView] = useState<ParcelView>({ live: null, toast: null });
  const hint = useStore(hintsStore, (s) => !s.done.parcel);
  useEffect(() => {
    for (const src of IMAGES) new Image().src = src;
    const watch = createParcelWatch({
      lifeTicks: LIFE_TICKS,
      onTap: (wave, reward, seconds) => analytics().parcelTap(wave, reward, seconds),
      onMissed: (wave) => analytics().parcelMissed(wave),
    });
    return battleStore.subscribe((snap) => {
      if (snap) setView(watch.update(snap, battleSpeed(appStore.get())));
    });
  }, []);
  const { live, toast } = view;
  return (
    <div class="parcel-zone" data-testid="parcel-zone">
      {live && (
        <button
          key={live.id}
          type="button"
          class="parcel hint-target"
          data-testid="parcel"
          data-parcel-id={live.id}
          aria-label={t("hud.parcel")}
          style={{ left: `${live.left}%`, top: `${live.top}%` }}
          onPointerDown={(e) => {
            e.preventDefault();
            onTap(live.id);
          }}
        >
          <span class="parcel__ring" aria-hidden="true" />
          <span class="parcel__fall" aria-hidden="true">
            <img class="parcel__chute" src={IMAGES[0]} alt="" draggable={false} />
            <img class="parcel__box" src={IMAGES[1]} alt="" draggable={false} />
          </span>
          {hint && <HintMark label={t("hud.hintParcel")} testId="hint-parcel" />}
        </button>
      )}
      {toast && <Toast toast={toast} t={t} />}
    </div>
  );
}
